import { Badge, Box, Flex, Group, Title, Center, Radio, SimpleGrid, Textarea } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useState } from 'react';
import { DEFAULT_ICON_SIZE, DRAWER_PADDING } from 'utils/general';
import { CenteredLoading } from 'components/CenteredLoading';
import { useReviewHeartRhythmMeasurement, useHeartRhythmMeasurementDetails } from 'hooks/api/useHeartRhythmData';
import { PortalButton } from 'components/Button/PortalButton';
import { BsArrowRight, BsFloppy } from 'react-icons/bs';
import {
  DIAGNOSIS_REVIEW_LABELS,
  HeartRhythmMeasurementDiagnosis,
  HeartRhythmMeasurementDiagnosisMap,
} from 'models/heartRhythm/measurementDiagnosis';
import { getHeartRhythmIndicatorColor, getUnreviewedMeasurementLabel } from 'utils/measurement';
import { HeartRhythmMeasurementStatus } from 'models/heartRhythm/measurement';

interface MeasurementIsRevisingProps {
  measurementId?: string;
  setIsRevising: Dispatch<SetStateAction<boolean>>;
}

export const MeasurementIsRevising = ({ measurementId, setIsRevising }: MeasurementIsRevisingProps) => {
  const { t } = useTranslation();
  const { data: selectedMeasurement } = useHeartRhythmMeasurementDetails({ measurementId });
  const isMeasurementReviewed = selectedMeasurement?.status === HeartRhythmMeasurementStatus.Reviewed;

  const [selectedDiagnosis, setSelectedDiagnosis] = useState(
    isMeasurementReviewed ? selectedMeasurement?.data?.diagnosis?.label[0] : undefined,
  );

  const [selectedDiagnosisNotes, setSelectedDiagnosisNotes] = useState(selectedMeasurement?.data?.diagnosis?.text);
  const { mutate: handleSaveAction, isPending } = useReviewHeartRhythmMeasurement({
    callback: () => setIsRevising(false),
  });

  const handleSave = () => {
    handleSaveAction({
      measurement: selectedMeasurement,
      label: selectedDiagnosis,
      text: selectedDiagnosisNotes,
    });
  };

  const shouldDisplayUpdatedDiagnosisLabel = () => {
    const doesSelectedLabelDifferFromDiagnosis = selectedMeasurement?.data?.diagnosis?.label[0] !== selectedDiagnosis;

    if (selectedDiagnosis && isMeasurementReviewed && !doesSelectedLabelDifferFromDiagnosis) {
      return false;
    }
    return true;
  };

  if (!selectedMeasurement) {
    return <CenteredLoading />;
  }

  return (
    <Box pt={'sm'} px={DRAWER_PADDING}>
      <Title my={'xl'} order={3}>
        {t('label_patient_revise')}
      </Title>
      <Center>
        <Flex>
          <Group>
            {selectedMeasurement?.data?.diagnosis && (
              <Badge
                color={getHeartRhythmIndicatorColor(
                  HeartRhythmMeasurementDiagnosisMap[selectedMeasurement?.data.diagnosis?.label[0]].indicator,
                )}
                size={'lg'}
                radius={'sm'}
                variant={'filled'}
              >
                {isMeasurementReviewed
                  ? t(`diagnosis_${selectedMeasurement?.data.diagnosis?.label[0]}`)
                  : getUnreviewedMeasurementLabel(selectedMeasurement?.data?.indicator)}
              </Badge>
            )}

            {selectedDiagnosis && shouldDisplayUpdatedDiagnosisLabel() && (
              <>
                <BsArrowRight size={DEFAULT_ICON_SIZE} />
                <Badge
                  color={getHeartRhythmIndicatorColor(HeartRhythmMeasurementDiagnosisMap[selectedDiagnosis].indicator)}
                  size={'lg'}
                  radius={'sm'}
                  variant={'filled'}
                >
                  {t(`diagnosis_${selectedDiagnosis}`)}
                </Badge>
              </>
            )}
          </Group>
        </Flex>
      </Center>
      <Radio.Group
        name={'diagnosisLabel'}
        value={selectedDiagnosis}
        onChange={(value) => setSelectedDiagnosis(value as HeartRhythmMeasurementDiagnosis)}
      >
        <SimpleGrid mt={'xl'} cols={2}>
          {DIAGNOSIS_REVIEW_LABELS.map((diagnosis) => (
            <Radio
              color={'var(--mantine-color-blue-5)'}
              key={diagnosis}
              label={t(`diagnosis_${diagnosis}`)}
              value={diagnosis}
              disabled={isPending}
            />
          ))}
        </SimpleGrid>
      </Radio.Group>
      <Textarea
        defaultValue={selectedDiagnosisNotes}
        onChange={(e) => setSelectedDiagnosisNotes(e.target.value)}
        my={'lg'}
        autosize
        minRows={2}
        placeholder={t('patients_additional_remarks')}
        disabled={isPending}
      />
      <Flex mt={'md'} justify={'flex-start'}>
        <Group gap={'xs'}>
          <PortalButton
            onClick={() => handleSave()}
            Icon={BsFloppy}
            label={t('label_patient_confirm_revise')}
            disabled={isPending}
            loading={isPending}
          />
          <PortalButton
            variant={'outline'}
            onClick={() => setIsRevising(false)}
            label={t('go_back_label')}
            disabled={isPending}
          />
        </Group>
      </Flex>
    </Box>
  );
};
